/**
 * Post-decision fulfilment rules (FR-5.18, HS2-HYDRJX) — shared so the server routes and the
 * editor agree on which lines can be ordered and how an order reads.
 */

/** Decision actions that buy the line, so it can be ordered. Reject, defer, and re-quote cannot. */
export const ORDERABLE_DECISION_ACTIONS = ['approve', 'negotiate', 'import'] as const;

/** Whether a line whose current decision has `action` can be marked Ordered. */
export function isOrderableDecision(action: string | null | undefined): boolean {
  return (ORDERABLE_DECISION_ACTIONS as readonly string[]).includes(action ?? '');
}

/** The parts of an order the status line names. Dates are ISO strings or Dates. */
export interface OrderSummaryInput {
  poNumber: string | null;
  expectedDeliveryAt: string | Date | null;
  vendorName?: string | null;
}

const dayFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** A short calendar day ("12 Oct") for an order date, or '' when absent or invalid. */
export function orderDay(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dayFormat.format(date);
}

/** "Ordered · PO 1234 · Acme · expected 12 Oct": what the line and the requester see. */
export function orderSummary(order: OrderSummaryInput): string {
  const parts = ['Ordered'];
  const po = order.poNumber?.trim() ?? '';
  // A reference that already reads "PO…" is not prefixed again.
  if (po !== '') parts.push(/^po\b/i.test(po) ? po : `PO ${po}`);
  const vendor = order.vendorName?.trim() ?? '';
  if (vendor !== '') parts.push(vendor);
  const expected = orderDay(order.expectedDeliveryAt);
  if (expected !== '') parts.push(`expected ${expected}`);
  return parts.join(' · ');
}

/** How far a demand's purchases have been ordered, for the requester's banner. */
export interface OrderProgress {
  /** Lines whose current decision buys them. */
  purchased: number;
  /** Of those, lines with a current order. */
  ordered: number;
  /** Of the ordered, lines delivered: received in full or closed short (FR-5.19). */
  delivered: number;
  /** The earliest expected delivery on or after `today` among the orders, as an ISO string. */
  nextExpected: string | null;
}

/**
 * Count ordered lines against purchased ones (FR-5.18). `today` is a `YYYY-MM-DD` day; a delivery
 * expected before it is past and not "next".
 */
export function orderProgress(
  decisions: readonly { lineItemId: string; action: string | null; isCurrent: boolean }[],
  orders: readonly {
    lineItemId: string;
    expectedDeliveryAt: string | null;
    quantityOrdered?: number | null;
    receivedQuantity?: number;
    closedShort?: boolean;
  }[],
  today: string,
): OrderProgress {
  const purchasedLines = new Set(
    decisions.filter((d) => d.isCurrent && isOrderableDecision(d.action)).map((d) => d.lineItemId),
  );
  const current = orders.filter((order) => purchasedLines.has(order.lineItemId));
  const isDelivered = (order: (typeof current)[number]): boolean =>
    isOrderFulfilled({
      quantityOrdered: order.quantityOrdered ?? null,
      receivedQuantity: order.receivedQuantity ?? 0,
      closedShort: order.closedShort ?? false,
    });
  const delivered = current.filter(isDelivered).length;
  // The next delivery is the earliest still outstanding.
  const upcoming = current
    .filter((order) => !isDelivered(order))
    .map((order) => order.expectedDeliveryAt)
    .filter((at): at is string => at !== null && at.slice(0, 10) >= today)
    .sort();
  return { purchased: purchasedLines.size, ordered: current.length, delivered, nextExpected: upcoming[0] ?? null };
}

/**
 * Whether an order has been delivered (FR-5.19, HS2-6AJZPE): closed short, or received at least the
 * quantity ordered. An order without a quantity is fulfilled by any receipt.
 */
export function isOrderFulfilled(order: {
  quantityOrdered: number | null;
  receivedQuantity: number;
  closedShort: boolean;
}): boolean {
  if (order.closedShort) return true;
  if (order.quantityOrdered === null) return order.receivedQuantity > 0;
  return order.receivedQuantity >= order.quantityOrdered;
}

/** Trim float noise from a summed quantity (0.1 + 0.2 → 0.3). */
export function roundQuantity(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** "Received 3 of 5", "Received in full", "Received 3 of 5 · rest closed short", or '' before any receipt. */
export function receiptSummary(order: {
  quantityOrdered: number | null;
  receivedQuantity: number;
  closedShort: boolean;
}): string {
  const received = roundQuantity(order.receivedQuantity);
  if (order.closedShort)
    return order.quantityOrdered === null || received === 0
      ? 'Closed short'
      : `Received ${String(received)} of ${String(order.quantityOrdered)} · rest closed short`;
  if (received === 0) return '';
  if (isOrderFulfilled(order)) return 'Received in full';
  return order.quantityOrdered === null
    ? `Received ${String(received)}`
    : `Received ${String(received)} of ${String(order.quantityOrdered)}`;
}

/** "Order with Acme failed: wrong grade. Being re-sourced." (FR-5.21, HS2-SFY5DY). */
export function failedOrderSummary(order: { vendorName?: string | null; failureReason: string | null }): string {
  const vendor = order.vendorName?.trim() ?? '';
  const reason = order.failureReason?.trim() ?? '';
  return `Order${vendor === '' ? '' : ` with ${vendor}`} failed${reason === '' ? '' : `: ${reason}`}. Being re-sourced.`;
}
